import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const MaterialsPreview = () => {
  const [activeMaterial, setActiveMaterial] = useState(0);

  const materials = [
    {
      id: 1,
      name: "Teca",
      description: "Madeira nobre de origem asiática, conhecida por sua resistência excepcional à umidade e durabilidade. Ideal para móveis externos e internos de alta qualidade.",
      image: "/assets/images/madeiras/teca.jpg",
      texture: "/assets/images/madeiras/teca.jpg",
      characteristics: [
        "Resistente à umidade",
        "Durabilidade excepcional",
        "Cor dourada natural",
        "Certificação sustentável"
      ],
      sustainability: "Manejo Sustentável",
      origin: "Ásia (Cultivo Brasileiro)",
      density: "0.65 g/cm³"
    },
    {
      id: 2,
      name: "Cumaru",
      description: "Uma das madeiras mais duras e resistentes do Brasil, com coloração castanho-avermelhada. Perfeita para móveis que precisam suportar uso intenso e condições adversas.",
      image: "/assets/images/madeiras/cumaru.jpg",
      texture: "/assets/images/madeiras/cumaru.jpg",
      characteristics: [
        "Extrema dureza",
        "Resistente a pragas",
        "Cor castanho-avermelhada",
        "Manejo sustentável"
      ],
      sustainability: "Manejo Sustentável",
      origin: "Amazônia Brasileira",
      density: "1.10 g/cm³"
    },
    {
      id: 3,
      name: "Freijó",
      description: "Madeira de cor clara e textura uniforme, oferece excelente trabalhabilidade e acabamento refinado. Ideal para móveis contemporâneos e sofisticados.",
      image: "/assets/images/madeiras/cumaru.jpg",
      texture: "/assets/images/madeiras/cumaru.jpg",
      characteristics: [
        "Textura uniforme",
        "Fácil trabalhabilidade",
        "Cor clara elegante",
        "Origem controlada"
      ],
      sustainability: "Manejo Sustentável",
      origin: "Amazônia Brasileira",
      density: "0.50 g/cm³"
    },
    {
      id: 4,
      name: "Jequitibá",
      description: "Madeira nobre brasileira de cor clara a amarelada, conhecida por sua leveza e resistência. Tradicionalmente usada em móveis finos e peças decorativas.",
      image: "/assets/images/madeiras/jequitiba.jpg",
      texture: "/assets/images/madeiras/jequitiba.jpg",
      characteristics: [
        "Leveza excepcional",
        "Cor clara a amarelada",
        "Boa estabilidade",
        "Cultivo responsável"
      ],
      sustainability: "Manejo Sustentável",
      origin: "Mata Atlântica",
      density: "0.45 g/cm³"
    },
    {
      id: 5,
      name: "Líptus Rosa",
      description: "Madeira de crescimento rápido com coloração rosada única, oferece excelente relação custo-benefício e beleza natural para móveis modernos.",
      image: "/assets/images/madeiras/jequitiba.jpg",
      texture: "/assets/images/madeiras/jequitiba.jpg",
      characteristics: [
        "Crescimento rápido",
        "Cor rosada única",
        "Sustentabilidade",
        "Boa trabalhabilidade"
      ],
      sustainability: "Manejo Sustentável",
      origin: "Plantações Brasileiras",
      density: "0.60 g/cm³"
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-muted rounded-full px-4 py-2 mb-6">
            <Icon name="TreePine" size={16} color="var(--color-primary)" />
            <span className="text-primary font-medium text-sm">Materiais Premium</span>
          </div>
          
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Madeiras Nobres Brasileiras
          </h2>
          
          <p className="text-text-secondary text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Trabalhamos exclusivamente com madeiras certificadas e sustentáveis, 
            selecionadas por sua qualidade excepcional e beleza única.
          </p>
        </div>

        {/* Materials Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Material Selection */}
          <div className="lg:col-span-1 space-y-4">
            {materials?.map((material, index) => (
              <div
                key={material?.id}
                onClick={() => setActiveMaterial(index)}
                className={`p-6 rounded-xl border cursor-pointer transition-all duration-300 ${
                  activeMaterial === index
                    ? 'bg-primary text-primary-foreground border-primary shadow-forest'
                    : 'bg-card border-border hover:border-primary hover:shadow-premium'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <Image
                      src={material?.texture}
                      alt={`Textura ${material?.name}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-playfair text-lg font-bold mb-2">
                      {material?.name}
                    </h3>
                    <div className={`inline-flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${
                      activeMaterial === index 
                        ? 'bg-white/20 text-white' :'bg-primary/10 text-primary'
                    }`}>
                      <Icon name="Shield" size={12} />
                      <span>{material?.sustainability}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Active Material Details */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-2xl overflow-hidden shadow-premium-lg">
              {/* Material Image */}
              <div className="relative h-80">
                <Image
                  src={materials?.[activeMaterial]?.image}
                  alt={materials?.[activeMaterial]?.name}
                  className="w-full h-full object-cover"
                />
                
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>
                
                <div className="absolute bottom-6 left-6 right-6 z-10">
                  <h3 className="font-playfair text-2xl font-bold text-white">
                    {materials?.[activeMaterial]?.name}
                  </h3>
                </div>
              </div>

              {/* Material Info */}
              <div className="p-8">
                <p className="text-text-secondary leading-relaxed mb-6">
                  {materials?.[activeMaterial]?.description}
                </p>

                {/* Characteristics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-playfair text-lg font-bold text-foreground mb-4">
                      Características
                    </h4>
                    <ul className="space-y-2">
                      {materials?.[activeMaterial]?.characteristics?.map((char, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <Icon name="Check" size={16} color="var(--color-primary)" />
                          <span className="text-text-secondary text-sm">{char}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-playfair text-lg font-bold text-foreground mb-4">
                      Especificações
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-text-secondary text-sm">Origem:</span>
                        <span className="text-foreground text-sm font-medium">
                          {materials?.[activeMaterial]?.origin}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary text-sm">Densidade:</span>
                        <span className="text-foreground text-sm font-medium">
                          {materials?.[activeMaterial]?.density}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary text-sm">Sustentabilidade:</span>
                        <span className="text-primary text-sm font-medium">
                          {materials?.[activeMaterial]?.sustainability}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
        </div>
      </div>
    </section>
  );
};

export default MaterialsPreview;